import { useId, type ReactElement } from 'react';
import { CaretDown, Check, CircleNotch, Eye, FloppyDisk, FolderSimple, Plus } from '@phosphor-icons/react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '~/components/ui/dropdown-menu';
import { Tip } from '~/components/ui/tip';

/**
 * FRONT-3077: the header controls the guided Builder and Pro Builder share —
 * the project switcher (folder icon + caret menu), the "New" button, the
 * Save App disc and the Preview eye (FRONT-3273). The markup and classes are the guided Builder's own, moved
 * here verbatim from `build-window.tsx`, so the two windows cannot
 * drift apart. Two callers: `build-window.tsx` and
 * `pro-builder-window.tsx`. The context meter beside them lives in
 * `context-meter.tsx`.
 *
 * Each control is disabled iff it has a reason (`disabledReason`), and that
 * reason is both its hover hint and its accessible description. A disabled
 * button takes no pointer events, so a disabled control's hint sits on a
 * wrapper span — the same mechanism the design system's `Button` uses — and
 * `Tip` never wraps a Radix `asChild` trigger (see `tip.tsx`).
 */

/** One jump target in the switcher's menu. */
export interface HeaderSwitcherOption {
  id: string;
  label: string;
}

/** The visually hidden description a disabled control points at with `aria-describedby`. */
function ReasonText({ id, reason }: { id: string; reason: string }) {
  return (
    <span id={id} className="sr-only">
      {reason}
    </span>
  );
}

/** A disabled control's hint has to sit on a wrapper: the disabled button itself never sees the hover. */
function HintedControl({ label, disabled, control }: { label: string; disabled: boolean; control: ReactElement }) {
  return <Tip label={label}>{disabled ? <span className="inline-flex shrink-0">{control}</span> : control}</Tip>;
}

/**
 * The switcher. The TRIGGER renders `title` (the attached project's own name),
 * never a list lookup that could silently fall back to the first option when
 * the list is stale, reordered or capped (FRONT-341); the menu is only ever
 * the jump targets, and the current one carries `aria-current` as well as the
 * tick — the tick is an icon, so without it a screen-reader user hears a list
 * of names with nothing saying which is open.
 *
 * An enabled trigger carries no tooltip: `Tip` is a plain component, not a
 * Radix Slot, so wrapping a `DropdownMenuTrigger asChild` child drops the
 * menu's cloned props and the button focuses without ever opening. The
 * accessible name carries the verb AND the current project instead.
 *
 * `loading` / `error` replace the items while a lazily-read list has no data
 * yet (Pro Builder reads its list only once the menu opens); the guided
 * Builder passes neither.
 */
export function HeaderProjectSwitcher({
  title,
  options,
  currentId,
  onSelect,
  switchLabel = 'Switch project',
  disabledReason = null,
  loading = false,
  loadingLabel = 'Loading…',
  error = null,
  onOpenChange,
  testId,
  itemTestId,
  itemIdAttribute,
}: {
  title: string;
  options: HeaderSwitcherOption[];
  /** The option that is open now (gets the tick and `aria-current`); null for none. */
  currentId: string | null;
  onSelect: (id: string) => void;
  /** The verb phrase before "— currently …" in the accessible name. */
  switchLabel?: string;
  disabledReason?: string | null;
  loading?: boolean;
  loadingLabel?: string;
  error?: string | null;
  onOpenChange?: (open: boolean) => void;
  testId?: string;
  itemTestId?: string;
  /** A `data-*` attribute each item carries its option id in (e.g. `data-folder`). */
  itemIdAttribute?: `data-${string}`;
}) {
  const reasonId = useId();
  const disabled = disabledReason !== null;
  const trigger = (
    <DropdownMenuTrigger asChild disabled={disabled}>
      <button
        type="button"
        aria-label={`${switchLabel} — currently ${title}`}
        aria-describedby={disabled ? reasonId : undefined}
        data-testid={testId}
        className="h-[34px] min-w-0 inline-flex items-center gap-2 rounded-[10px] px-2.5 text-[length:var(--text-sm)] text-foreground hover:bg-muted transition-colors disabled:opacity-50 disabled:pointer-events-none"
      >
        <FolderSimple size={16} className="text-muted-foreground shrink-0" aria-hidden />
        <span className="truncate max-w-[220px] font-semibold">{title}</span>
        <CaretDown size={12} weight="bold" className="text-muted-foreground shrink-0" aria-hidden />
      </button>
    </DropdownMenuTrigger>
  );
  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      {disabled ? (
        <>
          <Tip label={disabledReason}>
            <span className="inline-flex min-w-0">{trigger}</span>
          </Tip>
          <ReasonText id={reasonId} reason={disabledReason} />
        </>
      ) : (
        trigger
      )}
      <DropdownMenuContent align="start" className="w-64">
        {loading ? (
          <DropdownMenuItem disabled data-testid={testId ? `${testId}-loading` : undefined} className="gap-2">
            {loadingLabel}
          </DropdownMenuItem>
        ) : error !== null ? (
          <DropdownMenuItem disabled data-testid={testId ? `${testId}-error` : undefined} className="gap-2 text-destructive">
            {error}
          </DropdownMenuItem>
        ) : (
          options.map((o) => (
            <DropdownMenuItem
              key={o.id}
              onSelect={() => onSelect(o.id)}
              aria-current={o.id === currentId ? 'true' : undefined}
              data-testid={itemTestId}
              {...(itemIdAttribute ? { [itemIdAttribute]: o.id } : {})}
              className="gap-2"
            >
              <FolderSimple size={15} className="text-muted-foreground shrink-0" aria-hidden />
              <span className="truncate">{o.label}</span>
              {o.id === currentId && (
                <Check size={14} weight="bold" className="ml-auto shrink-0 text-primary" aria-hidden />
              )}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * "+ New". The visible word is "New"; the accessible name (`label`) stays the
 * fuller "New project" / "New app", which CONTAINS it — so a screen reader or
 * voice-control user still gets the noun.
 */
export function HeaderNewButton({
  onClick,
  label = 'New project',
  tooltip = 'Start a new project',
  disabledReason = null,
  testId,
}: {
  onClick: () => void;
  label?: string;
  tooltip?: string;
  disabledReason?: string | null;
  testId?: string;
}) {
  const reasonId = useId();
  const disabled = disabledReason !== null;
  return (
    <>
      <HintedControl
        label={disabledReason ?? tooltip}
        disabled={disabled}
        control={
          <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={label}
            aria-describedby={disabled ? reasonId : undefined}
            data-testid={testId}
            className="h-[34px] shrink-0 inline-flex items-center gap-2 rounded-[10px] px-2.5 text-[length:var(--text-sm)] font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50 disabled:pointer-events-none"
          >
            <Plus size={16} weight="bold" />
            New
          </button>
        }
      />
      {disabled && <ReasonText id={reasonId} reason={disabledReason} />}
    </>
  );
}

/**
 * The Save App disc. The design labels this "Save" — it is the only filled
 * button in the row, and "App" is already said by everything around it. The
 * ACCESSIBLE NAME stays "Save App": it contains the visible word and it is what
 * the browser specs address the control by.
 *
 * Still a raw <button> inside a Tip rather than the design system's `Button`,
 * deliberately — a browser spec hovers it for the exact tooltip text, and
 * keeping the mechanism identical is what stops a restyle from quietly
 * changing it. Disabled iff a save is running (`pending`, which also swaps the
 * disc for a spinner) or there is a `disabledReason`; the hint is the reason
 * when there is one, else `tooltip`.
 */
export function HeaderSaveButton({
  onClick,
  pending,
  disabledReason,
  tooltip,
  testId,
}: {
  onClick: () => void;
  pending: boolean;
  disabledReason: string | null;
  tooltip: string;
  testId?: string;
}) {
  const reasonId = useId();
  const disabled = pending || disabledReason !== null;
  return (
    <>
      <HintedControl
        label={disabledReason ?? tooltip}
        disabled={disabled}
        control={
          <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label="Save App"
            aria-describedby={disabledReason !== null ? reasonId : undefined}
            data-testid={testId}
            className="size-[34px] shrink-0 inline-flex items-center justify-center rounded-[10px] border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors active:scale-[0.96] disabled:opacity-50"
          >
            {pending ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />}
          </button>
        }
      />
      {disabledReason !== null && <ReasonText id={reasonId} reason={disabledReason} />}
    </>
  );
}

/** FRONT-3273: a click the page may take over — the primary button with no modifier. Any modifier, or another button, is the browser's own (a new tab). */
export function isPlainPrimaryClick(e: { metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean; button: number }): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

/**
 * The Preview eye (FRONT-3273: moved here from `build-window.tsx` so Pro
 * Builder shows the very same control). Icon only: `aria-label` keeps it a
 * NAMED control, and "Preview" is the name the browser specs address it by, so
 * it must not drift. A link to a new tab; `onPlainClick`, when given, takes over
 * a plain primary click only (the caller opens the app in a window beside its
 * own) — a modifier click always keeps the browser tab.
 */
export function HeaderPreviewLink({
  href,
  label,
  onPlainClick,
  testId,
}: {
  href: string;
  label: string;
  onPlainClick?: () => void;
  testId?: string;
}) {
  return (
    <Tip label={label}>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          if (!onPlainClick || !isPlainPrimaryClick(e)) return;
          e.preventDefault();
          onPlainClick();
        }}
        aria-label="Preview"
        data-testid={testId}
        className="size-[34px] shrink-0 inline-flex items-center justify-center rounded-[10px] border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      >
        <Eye size={16} />
      </a>
    </Tip>
  );
}
